import { ChartData, ChartDataset } from 'chart.js';
import { useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import {
  CubeDimensionEnum,
  FeatureEnum,
} from '../../../services/data/impl/cube/cubeSlice';
import {
  findHighestModelVersion,
  isBlueberryFeature,
} from '../../../services/data/impl/cube/cubeUtils';
import { convertSQLColumnUnits } from '../../commonUtils';
import useCurrServicesAndMeasures from '../../customerSummary/hooks/useCurrServicesAndMeasures';
import { DuckQueryReturnOneType } from '../../duckDB/hooks/useDuckQueryScanData';
import {
  selectCurrBlueberryClass,
  selectCurrFeature,
} from '../../select/selectSlice';
import { useUnits } from '../../settings/hooks/useUnits';
import { ScanDataEnum } from '../scanDataDefinitions';
import { useBlueberryQueryValues } from './useBlueberryQueryValues';
import { useGetIntervalAndMeasure } from './useGetIntervalAndMeasure';
import {
  OperatorEnum,
  operatorToExpressionMap,
  useScanDataQuery,
} from './useScanDataQuery';

export const DEFAULT_HEATMAP_COLORS = [
  '#097969',
  '#228B22',
  '#50C878',
  '#90EE90',
  '#E4D00A',
  '#FF5733',
] as [string, string, string, string, string, string];

export const blueberryColorConfigs: {
  [key: string]: [string, string, string, string, string, string];
} = {
  [FeatureEnum.blueberry_Blue]: [
    '#004caa',
    '#3370bb',
    '#6694cc',
    '#99b7dd',
    '#ccdbee',
    '#ffffe9',
  ],
  [FeatureEnum.blueberry_Purple]: [
    '#8849a5',
    '#a06db7',
    '#b892c9',
    '#cfb6db',
    '#e7dbed',
    '#ffffe9',
  ],
  [FeatureEnum.blueberry_Yellow]: [
    '#e7388a',
    '#ec60a1',
    '#f188b9',
    '#f5afd0',
    '#fad7e8',
    '#ffffe9',
  ],
  [FeatureEnum.blueberry_Green]: [
    '#4d754d',
    '#54a254',
    '#70c470',
    '#9ad39a',
    '#c0e4c0',
    '#ffffe9',
  ],
  [FeatureEnum.blueberry]: DEFAULT_HEATMAP_COLORS,
  [FeatureEnum.flower]: DEFAULT_HEATMAP_COLORS,
};

export const getBlueberryColorsDistributionByRawData = (
  stdDev: number,
  mean: number,
  className: FeatureEnum | null
) => {
  const colors =
    className && Object.hasOwn(blueberryColorConfigs, className)
      ? blueberryColorConfigs[className]
      : DEFAULT_HEATMAP_COLORS;

  const values = [
    mean - -2 * stdDev,
    mean - -1 * stdDev,
    mean - 0 * stdDev,
    mean - 1 * stdDev,
    mean - 2 * stdDev,
  ].filter((value) => value > 0);

  const ranges: [number, number, string][] = [];

  values.forEach((value, i) => {
    ranges.push([value, i !== 0 ? values[i - 1] : Number.MAX_VALUE, colors[i]]);
  });

  ranges.push([0, values[values.length - 1], colors[values.length]]);
  return ranges.reverse();
};

export interface BlueberryIntervalQuery {
  interval: number;
  interval_value: number;
}
/**
 * Returns blueberry class distribution options for bar chart
 * @returns {DuckQueryReturnListType<ChartData<'bar'>>}
 */
export const useBlueberryDistributionByClass = (): DuckQueryReturnOneType<
  ChartData<'bar'>
> => {
  const currFeature = useAppSelector(selectCurrFeature);
  const currBlueberryClass = useAppSelector(selectCurrBlueberryClass);
  const units = useUnits();
  const { interval, measure } = useGetIntervalAndMeasure();
  const { servicesAndMeasuresWithFeatureFilter } = useCurrServicesAndMeasures();

  // For "blueberry", only model version and name are needed. Class color is arbitrary (e.g., blueberry_Blue). Model versions are synced.
  const latestService = findHighestModelVersion(
    servicesAndMeasuresWithFeatureFilter.availableServicesRaw,
    [FeatureEnum.blueberry_Blue]
  );

  const {
    data: stdDevMeanData,
    isLoading: stdDevLoading,
    error: stdDevError,
  } = useBlueberriesMeanStdDev();

  const filters = [];

  if (latestService) {
    filters.push(
      {
        col: ScanDataEnum.model_name,
        op: OperatorEnum.eq,
        value: latestService[CubeDimensionEnum.modelName],
      },
      {
        col: ScanDataEnum.model_version,
        op: OperatorEnum.eq,
        value: latestService[CubeDimensionEnum.modelVersion],
      }
    );
  }

  const queryValues = useBlueberryQueryValues();

  const blueberryFeatureSearchFilter = {
    col: ScanDataEnum.class_name,
    op: queryValues.length === 1 ? OperatorEnum.eq : OperatorEnum.in,
    value:
      queryValues.length === 1
        ? (queryValues.at(0) as string)
        : (queryValues as string[]),
  };

  filters.push(blueberryFeatureSearchFilter);

  // aggregate subquery
  /*
   * if average value is lesser than 1 - make interval separated by 1/100 values like 0.01, 0.02, etc.
   * if average value is lesser than 10 - make interval separated by 1/10 values like 0.1, 0.2, etc.
   * if average value is lesser than 100 - make interval separated by rounded values like 1, 2, 3, etc.
   * if average value is greater or equals 100 - make interval separated by each 10 values, like 10, 20, 30, etc.
   */
  const cols = [
    `(CASE
    WHEN ${stdDevMeanData?.mean} < 1 THEN FLOOR(${interval} * 100) / 100
    WHEN ${stdDevMeanData?.mean} < 10 THEN FLOOR(${interval} * 10) / 10
    WHEN ${stdDevMeanData?.mean} < 100 THEN FLOOR(${interval})
    ELSE FLOOR(${interval} / 10) * 10
    END) as interval`, // group buckets by 10
    // `SUM(raw_count) as interval_value`,
    `COUNT(${measure}) as interval_value`,
  ];

  // use subquery with counts per classes/class
  const from = `(
  SELECT 
    SUM(${convertSQLColumnUnits(
      measure as ScanDataEnum,
      units,
      false
    )}) as ${measure}
  FROM scanData
  WHERE
  ${filters
    .map(({ col, value, op }) => operatorToExpressionMap[op](col, value))
    .join('\n\tAND ')}
  GROUP BY point_id
  )`;

  const { data, isLoading, error, query } =
    useScanDataQuery<BlueberryIntervalQuery>({
      cols,
      from,
      // filters,
      group: ['interval'],
      order: [['interval', 'asc']],
      skip:
        !latestService ||
        !interval ||
        !measure ||
        !isBlueberryFeature(currFeature) ||
        !stdDevMeanData?.mean,
      disableClassFilter: true,
      disableUnitsTransform: true,
    });

  // didn't finish validation and don't think this is worth the effort for blueberries bc too complex
  // useBlueberryValidateDistributionResults(data, latestService);

  if (!data || !stdDevMeanData) {
    return {
      data: null,
      isLoading: isLoading || stdDevLoading,
      error: error || stdDevError,
      query,
    };
  }

  const ranges = getBlueberryColorsDistributionByRawData(
    stdDevMeanData.std_dev,
    stdDevMeanData.mean,
    currBlueberryClass
  );

  const datasets: ChartDataset<'bar', number[]>[] = [
    {
      label: 'interval',
      data: data.map(({ interval_value }) => Number(interval_value)) || [],
      // fill: false,
      backgroundColor: data.map(
        ({ interval }) =>
          ranges.find(
            (range) => range[0] <= interval && range[1] >= interval
          )?.[2] || '#000'
      ),
    },
  ];

  const chartData: ChartData<'bar'> = {
    datasets,
    labels: data.map(({ interval }) => interval),
  };

  return {
    data: chartData,
    isLoading: isLoading || stdDevLoading,
    error: error || stdDevError,
    query,
  };
};

interface BlueberryStdDevMeanData {
  std_dev: number;
  mean: number;
}

export const useBlueberriesMeanStdDev =
  (): DuckQueryReturnOneType<BlueberryStdDevMeanData> => {
    const currFeature = useAppSelector(selectCurrFeature);
    const currBlueberryClass = useAppSelector(selectCurrBlueberryClass);
    const units = useUnits();
    const { servicesAndMeasuresWithFeatureFilter } =
      useCurrServicesAndMeasures();
    const { measure } = useGetIntervalAndMeasure();
    const queryValues = useBlueberryQueryValues();

    // For "blueberry", only model version and name are needed. Class color is arbitrary (e.g., blueberry_Blue). Model versions are synced.
    const latestService = findHighestModelVersion(
      servicesAndMeasuresWithFeatureFilter.availableServicesRaw,
      [FeatureEnum.blueberry_Blue]
    );

    const filters = useMemo(() => {
      const newFilters = [];

      if (latestService) {
        newFilters.push(
          {
            col: ScanDataEnum.model_name,
            op: OperatorEnum.eq,
            value: latestService[CubeDimensionEnum.modelName],
          },
          {
            col: ScanDataEnum.model_version,
            op: OperatorEnum.eq,
            value: latestService[CubeDimensionEnum.modelVersion],
          }
        );
      }

      const blueberryFeatureSearchFilter = {
        col: ScanDataEnum.class_name,
        op: queryValues.length === 1 ? OperatorEnum.eq : OperatorEnum.in,
        value:
          queryValues.length === 1
            ? (queryValues.at(0) as string)
            : (queryValues as string[]),
      };

      newFilters.push(blueberryFeatureSearchFilter);

      return newFilters;
    }, [latestService, currBlueberryClass, queryValues]);

    const from = useMemo(() => {
      // use subquery with counts per classes/class
      const newFrom = `(
      SELECT 
        SUM(${convertSQLColumnUnits(
          measure as ScanDataEnum,
          units,
          false
        )}) as ${measure}
      FROM scanData
      WHERE
      ${filters
        .map(({ col, value, op }) => operatorToExpressionMap[op](col, value))
        .join('\n\tAND ')}
      GROUP BY point_id
      )`;

      return newFrom;
    }, [filters, units]);

    // aggregate subquery
    // TODO: validate if sum of mean and avg of std. dev. is correct operation
    const cols = useMemo(
      () => [`AVG(${measure}) as mean`, `stddev_samp(${measure}) as std_dev`],
      [measure]
    );

    const result = useScanDataQuery<BlueberryStdDevMeanData>({
      cols: cols,
      from,
      skip: !latestService || !measure || !isBlueberryFeature(currFeature),
      disableClassFilter: true,
      disableUnitsTransform: true,
    });

    return {
      ...result,
      data: result.data ? result.data[0] : null,
    };
  };
